import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { CircleCheck, CircleSlash, CircleX, FileQuestion, Users } from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import DataTable from '../../components/DataTable'
import FilterBar from '../../components/FilterBar'
import Meter from '../../components/Meter'
import PageHeader from '../../components/PageHeader'
import PageLoader from '../../components/PageLoader'
import StatStrip from '../../components/StatStrip'
import StatusBadge from '../../components/StatusBadge'
import { DeniedState, EmptyState, ErrorState } from '../../components/States'
import TabBar from '../../components/TabBar'
import useGoBack from '../../components/useGoBack'
import useHashTab from '../../components/useHashTab'
import { useOnChange, useRemembered } from '../../components/listMemory'
import { usePageParam } from '../../components/useListParams'
import { useToast } from '../../components/toastContext'
import { getAssessment, getAssessmentAnswers, getAssessmentReport } from './engagementService'
import { ChartPanel, SplitBar } from '../reports/ReportBits'
import { ROW_WINDOW, rateBarsHeight, rateBarsOption } from '../reports/reportCharts'
import { useTheme } from '../../lib/theme'
import '../reports/reports.css'
import { assessmentsStrings as A } from './strings'
import './engagement.css'
import { serverDetail } from '../../lib/i18n'

const S = A.report

/** The three scopes are one endpoint; `answers` is the other. */
const PEOPLE = ['passed', 'failed', 'unattended']
const TABS = [...PEOPLE, 'answers']

/**
 * تقارير القياس — who sat one assessment, and how it went.
 *
 * THE LEGACY HAS NO SCREEN OF ITS OWN FOR THIS. Its row action sends the assessment to
 * `Exams?assessmentId={id}` — the COURSE exam report, borrowed — where two of the five tabs
 * are hidden for being course-shaped and two more, `الناجحون` and `الراسبون`, are hidden by
 * accident: that view gates them on `hasExam`, which the controller never sets for an
 * assessment. So the legacy lists a pass count and a failure count on the row and then
 * refuses to say whose they are. All four tabs are here.
 *
 * ONE SCOPE AT A TIME, PAGED ON THE SERVER. The audience is whole departments and the
 * unattended tab is usually most of it; the counts for the two tabs not on screen are asked
 * for with `length: 0`, which costs a COUNT and no rows.
 */
export default function AssessmentReportPage() {
  const { id } = useParams()
  const goBack = useGoBack('/assessments-management')
  const toast = useToast()
  /* The topic chart bakes resolved colours into its option; a theme switch rebuilds it. */
  const theme = useTheme()

  const [assessment, setAssessment] = useState(null)
  /* The load's own refusal — a 403 or a transport fault, as opposed to "that id is not an
     assessment", which the API answers 200 with `Result: false`. The two need different
     screens, so they are tracked apart. */
  const [loadError, setLoadError] = useState(null)
  const [counts, setCounts] = useState(null)
  const [audience, setAudience] = useState(0)
  const [loading, setLoading] = useState(true)

  const [tab, setTab] = useHashTab('passed', TABS)
  const [query, setQuery] = useRemembered(`assessment-report:${id}:query`, '')
  const [page, setPage] = usePageParam()
  const [pageSize, setPageSize] = useRemembered(`assessment-report:${id}:pageSize`, 25)
  /* The order, owned here with the page and sent to the server. Null is the handler's own
     order — best score first on the two result tabs, by name on the unattended. */
  const [sort, setSort] = useRemembered(`assessment-report:${id}:sort`, null)

  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [rowsLoading, setRowsLoading] = useState(false)

  const [answers, setAnswers] = useState(null)
  const [answersLoading, setAnswersLoading] = useState(false)

  const fail = useCallback(
    (error) => toast.error(serverDetail(error?.message) || A.fail.report),
    [toast],
  )

  /* The head of the page and the three totals, once. Nothing on this screen writes, so the
     counts cannot change under it. */
  useEffect(() => {
    let cancelled = false
    setLoading(true)

    Promise.all([
      getAssessment(id),
      ...PEOPLE.map((scope) => getAssessmentReport(id, scope, { length: 0 })),
    ])
      .then(([found, ...scopes]) => {
        if (cancelled) return
        setLoadError(null)
        setAssessment(found)
        setCounts(Object.fromEntries(PEOPLE.map((scope, i) => [scope, scopes[i].scopeTotal])))
        setAudience(scopes[0].audienceTotal)
        setLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setLoadError(error)
        setLoading(false)
        fail(error)
      })

    return () => {
      cancelled = true
    }
  }, [id, fail])

  /* A different tab is a different list, and a search is a different list again — both start
     at their first page. On a CHANGE only: arriving with `?page=3#failed` means page 3. */
  useOnChange(() => setPage(1), [tab, query])

  useEffect(() => {
    if (!PEOPLE.includes(tab)) return undefined

    let cancelled = false
    setRowsLoading(true)

    getAssessmentReport(id, tab, {
      start: (page - 1) * pageSize,
      length: pageSize,
      search: query,
      sort,
    })
      .then((answer) => {
        if (cancelled) return
        setRows(answer.rows)
        setTotal(answer.total)
        /* The tab's own count, corrected by the same call that fills the table — a search
           narrows `total`, never this. */
        setCounts((previous) => ({ ...previous, [tab]: answer.scopeTotal }))
        setRowsLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setRows([])
        setTotal(0)
        setRowsLoading(false)
        fail(error)
      })

    return () => {
      cancelled = true
    }
  }, [id, tab, page, pageSize, query, sort, fail])

  /* The questions, on first visit only — they are the same list however the people tabs are
     paged, and there are tens of them. */
  useEffect(() => {
    if (tab !== 'answers' || answers) return undefined

    let cancelled = false
    setAnswersLoading(true)

    getAssessmentAnswers(id)
      .then((loaded) => {
        if (cancelled) return
        setAnswers(loaded)
        setAnswersLoading(false)
      })
      .catch((error) => {
        if (cancelled) return
        setAnswers([])
        setAnswersLoading(false)
        fail(error)
      })

    return () => {
      cancelled = true
    }
  }, [id, tab, answers, fail])

  const stats = useMemo(() => {
    const passed = counts?.passed ?? 0
    const failed = counts?.failed ?? 0
    const unattended = counts?.unattended ?? 0

    return [
      {
        id: 'audience',
        icon: Users,
        label: S.stats.audience,
        /* The membership of the assessment's groups, from the server — not these three
           added up, which double-counts anyone who sat it twice. */
        value: audience,
        tone: 'violet',
      },
      { id: 'passed', icon: CircleCheck, label: S.stats.passed, value: passed, tone: 'success' },
      { id: 'failed', icon: CircleX, label: S.stats.failed, value: failed, tone: 'danger' },
      {
        id: 'unattended',
        icon: CircleSlash,
        label: S.stats.unattended,
        value: unattended,
        tone: 'warning',
      },
    ]
  }, [counts, audience])

  /* The answers pooled by topic, weakest first — what the questions table says one row at a time,
     said once per topic. Tens of questions fold into a handful of bars. */
  const topics = useMemo(() => {
    const byTopic = new Map()
    for (const row of answers ?? []) {
      const name = row.category || S.columns.category
      const entry = byTopic.get(name) ?? { name, answered: 0, correct: 0 }
      entry.answered += row.answers ?? 0
      entry.correct += row.correct ?? 0
      byTopic.set(name, entry)
    }
    const rows = [...byTopic.values()]
      .filter((topic) => topic.answered > 0)
      .map((topic) => ({
        name: topic.name,
        rate: { value: Math.round((topic.correct / topic.answered) * 1000) / 10, numerator: topic.correct, denominator: topic.answered },
      }))
      .sort((a, b) => a.rate.value - b.rate.value)
    const answered = rows.reduce((sum, row) => sum + row.rate.denominator, 0)
    const right = rows.reduce((sum, row) => sum + row.rate.numerator, 0)
    const opts = {
      valueLabel: S.columns.correct,
      average: answered ? Math.round((right / answered) * 1000) / 10 : null,
      averageLabel: S.topicsAverage,
    }
    return {
      rows,
      option: rows.length ? rateBarsOption(rows, opts) : null,
      whole: rows.length > ROW_WINDOW ? rateBarsOption(rows, { ...opts, all: true }) : null,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answers, theme])

  const peopleColumns = useMemo(() => {
    const columns = [
      { key: 'number', header: S.columns.index, width: '64px', align: 'center', sortable: false },
      { key: 'fullName', header: S.columns.name },
      { key: 'userName', header: S.columns.userName },
      { key: 'email', header: S.columns.email, render: (row) => <bdi dir="ltr">{row.email}</bdi> },
      { key: 'department', header: S.columns.department },
    ]

    /* The unattended have no exam behind them, so those two columns would be a pair of
       empty ones — the employee number is what that tab is chased with instead. */
    if (tab === 'unattended') {
      return [...columns, { key: 'employeeId', header: S.columns.employeeId, align: 'center' }]
    }

    return [
      ...columns,
      {
        key: 'examDate',
        header: S.columns.examDate,
        align: 'center',
        render: (row) => <bdi dir="ltr">{row.examDate}</bdi>,
      },
      {
        key: 'percentage',
        header: S.columns.score,
        align: 'center',
        width: '110px',
        render: (row) => (
          <StatusBadge tone={row.passed ? 'success' : 'danger'}>
            <bdi dir="ltr">{`${Math.round(row.percentage ?? 0)}%`}</bdi>
          </StatusBadge>
        ),
      },
    ]
  }, [tab])

  const answerColumns = useMemo(
    () => [
      { key: 'number', header: S.columns.index, width: '64px', align: 'center', sortable: false },
      { key: 'question', header: S.columns.question },
      { key: 'category', header: S.columns.category },
      { key: 'difficulty', header: S.columns.difficulty, align: 'center' },
      { key: 'answers', header: S.columns.answers, align: 'center', width: '110px' },
      {
        key: 'correctPercentage',
        header: S.columns.correct,
        width: '210px',
        render: (row) => (
          <span className="assess-report__ratio">
            <Meter
              value={row.correctPercentage}
              tone={row.correctPercentage >= 50 ? 'success' : 'danger'}
              label={S.columns.correct}
              size="sm"
            />
            <bdi className="assess-report__ratio-value" dir="ltr">
              {`${row.correctPercentage}%`}
            </bdi>
          </span>
        ),
      },
    ],
    [],
  )

  if (loading) return <PageLoader />

  /* A TRANSPORT FAULT, not a refusal. `api` throws for both: `code: 'RESULT'` is the server
     answering 200 with `Result: false` — here always "Assessment not found" — while anything
     else is a 403 or a broken request, and those two want different screens. */
  if (loadError && loadError.code !== 'RESULT') {
    return (
      <>
        <PageHeader title={S.title} />
        {loadError.denied ? (
          <DeniedState description={loadError.message} />
        ) : (
          <ErrorState title={A.fail.report} description={loadError.message} />
        )}
      </>
    )
  }

  /* NO ASSESSMENT, NO REPORT. A report URL carrying an id nobody owns used to draw the whole
     page — title "تقرير القياس", every figure 0, an empty table — which reads exactly like a
     real assessment nobody has sat. Derived from the loaded row rather than from a flag, so
     it holds whether `get` answered `Result: false` or refused outright. */
  if (!assessment?.id) {
    return (
      <>
        <PageHeader title={S.title} />
        <EmptyState
          icon={FileQuestion}
          title={S.notFound}
          description={S.notFoundHint}
          action={
            <Button as={Link} to="/assessments-management">
              {S.backToList}
            </Button>
          }
        />
      </>
    )
  }

  return (
    <>
      {/* The assessment names the page and the meta line says what it was: when it ran,
          what it took to pass, how many questions. */}
      <PageHeader
        title={assessment?.title || S.title}
        subtitle={
          assessment ? (
            /* Each figure isolated: joined into one string, "70%" beside an Arabic label was
               laid out as "%70". */
            <>
              <bdi dir="ltr">{`${assessment.displayStartDate} — ${assessment.displayEndDate}`}</bdi>
              {' · '}
              {S.passMark}: <bdi dir="ltr">{assessment.passLimit}%</bdi>
              {' · '}
              {S.questionsCount}: <bdi dir="ltr">{assessment.questionsCount}</bdi>
            </>
          ) : (
            S.title
          )
        }
        onBack={goBack}
      />

      <StatStrip items={stats} label={S.title} variant="band" />

      {/* The same three as shares of each other — one bar, so "most never sat it" is seen. */}
      {counts && (counts.passed || counts.failed || counts.unattended) ? (
        <Card title={S.split}>
          <SplitBar
            label={S.split}
            parts={[
              { key: 'passed', label: S.stats.passed, value: counts.passed ?? 0, tone: 'success' },
              { key: 'failed', label: S.stats.failed, value: counts.failed ?? 0, tone: 'failure' },
              { key: 'unattended', label: S.stats.unattended, value: counts.unattended ?? 0, tone: 'absent' },
            ]}
          />
        </Card>
      ) : null}

      <Card>
        <TabBar
          items={TABS.map((one) => ({ id: one, label: S.tabs[one] }))}
          value={tab}
          onChange={setTab}
          ariaLabel={S.title}
        />

        {tab === 'answers' ? (
          <>
          {topics.option && (
            <div className="assess-report__topics">
              <ChartPanel
                title={S.topics}
                hint={S.topicsHint}
                option={topics.option}
                height={rateBarsHeight(topics.rows.length)}
                more={
                  topics.whole
                    ? { total: topics.rows.length, option: topics.whole, height: rateBarsHeight(topics.rows.length, true) }
                    : null
                }
                summary={S.topics}
              />
            </div>
          )}
          <DataTable
            /* Client-side here, unlike the three people tabs: an assessment's exam is built
               from question rules, so this is tens of rows and the whole list is already in
               hand. Out of the address, which the people tabs own. */
            pageParam={null}
            columns={answerColumns}
            rows={answers ?? []}
            loading={answersLoading}
            getRowId={(row) => row.id}
            emptyMessage={S.empty.answers}
            initialSort={{ key: 'correctPercentage', dir: 'asc' }}
          />
          </>
        ) : (
          <>
            <FilterBar
              search={{ value: query, onChange: setQuery, placeholder: S.searchUsers }}
            />
            <DataTable
              /* The SCREEN owns `?page` — it has to, since the request that fills this
                 table is made up here. */
              pageParam={null}
              columns={peopleColumns}
              rows={rows}
              loading={rowsLoading}
              query={query}
              getRowId={(row) => row.id}
              emptyMessage={S.empty[tab]}
              server={{
                page,
                pageSize,
                total,
                sort,
                onChange: (next) => {
                  setPage(next.page)
                  setPageSize(next.pageSize)
                  setSort(next.sort)
                },
              }}
            />
          </>
        )}
      </Card>
    </>
  )
}
